/**
 * Domain types for the custom-lexicon feature (VE-24948 / U5).
 *
 * A lexicon is an org-scoped, named list of terms and patterns to match against
 * document text. All org users may create, edit, delete and apply them
 * (U5-FR3) — enforcement is the platform's SDO role check, NOT this app's, so
 * nothing here or in the UI implements permission logic (SEC-R5).
 *
 * Storage: ONE aiWARE structured-data object per lexicon, with entries embedded
 * as an array. That choice is load-bearing and non-obvious, so it is recorded
 * here rather than only in the design docs: the aiWARE schema has no bulk SDO
 * mutation and `updateStructuredData` replaces `data` wholesale, so
 * one-SDO-per-entry would make an N-row CSV import N un-transacted round trips
 * — and a partial failure would leave a half-imported lexicon, which U5-FR12
 * forbids. Embedding makes an import ONE atomic write. The cost is
 * read-modify-write, closed by `Lexicon.revision`.
 */

/**
 * Two entry types, not three.
 *
 * The requirement vocabulary is "keyword, phrase, or pattern", but keyword and
 * phrase share one matching path — `findKeywordMatches` already normalizes
 * internal whitespace, so a "phrase" is just a keyword containing a space.
 * A third enum value would be a state the panel, the CSV importer, the audit
 * trail and the snapshot all have to carry while nothing branches on it.
 *
 * The CSV importer still ACCEPTS `keyword` and `phrase` and maps both to
 * `term`, so a CSV written to the acceptance criteria imports unchanged.
 */
export type LexiconEntryType = 'term' | 'regex';

export interface LexiconEntry {
  /** Stable for the life of the entry; never reused after deletion (BR-8). */
  readonly entryId: string;
  /** Literal text for `term`; an ECMAScript pattern source for `regex`. */
  readonly term: string;
  readonly type: LexiconEntryType;
  /**
   * `term` entries only. Supplying either for a `regex` entry is a validation
   * error rather than a silent ignore (BR-13) — a regex carries its own
   * case and boundary semantics, and accepting a flag we then ignore would
   * quietly mislead the author.
   */
  readonly caseSensitive?: boolean;
  readonly wholeWord?: boolean;
  /**
   * Optional reference to an existing redaction code (U5-FR8). Reference only
   * — U5 never creates or edits codes (non-goal N3).
   */
  readonly redactionCodeId?: string;
  /**
   * Soft-delete tombstone. Absent or false means the entry is live.
   *
   * WHY A FLAG AND NOT A SPLICE. The delete control is an unguarded icon on
   * every row — one click, no confirmation — sitting in a list an admin scrolls
   * and clicks around in. Losing a term that way is silent and, until Save, not
   * obviously recoverable; after Save it is gone from the lexicon entirely and
   * only a truncated audit snapshot remembers it.
   *
   * A deleted entry is NOT a matching entry. Every read that feeds the matcher,
   * the apply panel or an entry count filters on this — see `liveEntries`, and
   * use it rather than hand-rolling `.filter(e => !e.isDeleted)`, because the
   * one that gets missed is the one where a term an agency deliberately removed
   * keeps redacting their documents.
   *
   * Tombstones still occupy the entries array, which the published schema caps
   * at `maxItems: 5000` (= `LEXICON_MAX_ENTRIES`). That is what Purge is for.
   */
  readonly isDeleted?: boolean;
}

/** The entries that still count: not soft-deleted. */
export function liveEntries<T extends { isDeleted?: boolean }>(
  entries: readonly T[]
): T[] {
  return entries.filter((e) => !e.isDeleted);
}

/** The tombstones, for the drawer's "View Deleted Entries" view. */
export function deletedEntries<T extends { isDeleted?: boolean }>(
  entries: readonly T[]
): T[] {
  return entries.filter((e) => !!e.isDeleted);
}

/**
 * What kind of record an SDO in the lexicon registry is.
 *
 * ONE REGISTRY, TWO KINDS. A lexicon's metadata and its entries are separate
 * records, because `structuredDataObjects` returns `data` as a single JSON
 * scalar — there are no subfields to select, so listing lexicons to show their
 * names would otherwise transfer every entry of every one of them (up to
 * 125,000 for a 25-row page at the BR-16 ceiling). The only way to make the
 * list cheap is for the entries to live in a different record.
 *
 * The two kinds share a registry and are told apart by this field, which the
 * list query filters on. That mechanism is not a guess: `watermarks/services`
 * ships the same `structuredDataObjects(..., filter: $filter)` call today.
 *
 * VALUES ARE STRINGS AND EVERY RECORD MUST CARRY ONE. An SDO filter matches the
 * stored value exactly, so a record written without a filtered key matches no
 * filter and disappears from the list — the failure the watermark library hit
 * when it added `isDeleted` to rows that predated it.
 *
 * THIS HAS ALREADY HAPPENED ONCE HERE, and the lesson is worth the space.
 * `isDeleted` became a filtered key after the feature had been running on
 * development clusters, and every lexicon written before that vanished from
 * both lists at once — intact, and invisible. Marking a key `required` in the
 * published schema stops the NEXT such record from being written; it does
 * nothing for the ones already there. Adding a key to a filter therefore means
 * a backfill, not just a republish.
 */
export type LexiconRecordKind = 'lexicon' | 'entries';

/**
 * The summary half: everything except the entries.
 *
 * What the settings table, the upload-time selector and the editor's apply
 * panel all list. `entryCount` is a CACHE of the detail record's length — see
 * `entriesId` — and is allowed to lag.
 */
export interface LexiconSummary {
  /** SDO id of the summary record. This is the lexicon's id. */
  readonly id: string;
  readonly name: string;
  /** Guards renames and deletes (BR-6). Entry writes guard the DETAIL's own. */
  readonly revision: number;
  /**
   * SDO id of the record holding this lexicon's entries.
   *
   * Written when the lexicon is created, and never changed. A summary without
   * one is a lexicon whose detail write succeeded and whose summary write then
   * recorded nothing — which cannot happen in the create order used here, but
   * is parsed leniently rather than dropping the whole lexicon.
   */
  readonly entriesId: string;
  /**
   * How many LIVE entries the detail record holds — tombstones excluded.
   *
   * This is the number every surface shows as "Entries (N)", so it counts what
   * a reader would count: an entry that has been deleted is not in the lexicon,
   * whatever the array still holds.
   *
   * A cache, and deliberately so. It is written with the summary, which is
   * written BEFORE the detail, so a failed detail write leaves it describing an
   * intended state that never landed. Healed whenever a detail is read and the
   * two disagree; see `fetchLexiconById`.
   */
  readonly entryCount: number;
  /**
   * How many TOMBSTONES the detail record holds.
   *
   * The other half of the same pair, and it exists for the same reason: the
   * drawer's "View Deleted Entries (N)" is a number about the entries, and
   * having to load every entry to produce a number is exactly what the storage
   * split was done to stop. Kept in step with `entryCount` — written together,
   * healed together — so the two never disagree about the same array.
   *
   * Their sum is the array's length, which is what the schema's `maxItems`
   * counts: a tombstone occupies a slot until it is purged.
   */
  readonly deletedEntryCount: number;
  readonly createdDateTime: string;
  readonly modifiedDateTime: string;
  /**
   * Email of the user who created the lexicon, AS THE CLIENT REPORTED IT.
   *
   * Not provenance, and a "Created by" column must not be read as such: the
   * platform stores no author of its own for an SDO, so there is nothing to
   * corroborate this against, and every field on the record is writable through
   * the public API by any org member under the flat permission model (U5-FR3).
   * It answers "whose is this?", not "who is accountable for this" — the audit
   * trail is the only thing that answers the second. The watermark library
   * reached the same conclusion about its own `createdBy`; this is the same
   * caveat, not a different one.
   *
   * Preserved across every later write (`updateLexiconGuarded` carries it from
   * the record it read), so it stays the FIRST claim rather than the most
   * recent one.
   */
  readonly createdBy: string;
  /** Email of the last writer, on the same terms as `createdBy`. */
  readonly modifiedBy: string;

  /**
   * Soft delete for the LEXICON itself (BR-5, as amended).
   *
   * NOT OPTIONAL, and that is the point. The live list and the Deleted
   * Lexicons list are two filtered queries — `isDeleted: 'false'` and
   * `'true'` — and an SDO filter matches the STORED value, so a record that
   * omitted this key would appear in NEITHER list. It is required in the
   * published schema for exactly that reason, and every write path stamps it,
   * including the detail record, which carries a constant `false` where it
   * means nothing. See `docs/schemas/LexiconSchema.md`, "Required keys".
   *
   * A deleted lexicon appears in no picker and no dropdown, and does not hold
   * its NAME — the name is free for reuse immediately, which is why restoring
   * is the operation that can be refused rather than deleting. Documents that
   * already applied it are unaffected, because their detections carry snapshots
   * (U5-FR2), which is the same property that makes a watermark's soft delete
   * safe.
   */
  readonly isDeleted: boolean;

  // --- library housekeeping -------------------------------------------------
  // Three optional fields, deliberately mirroring the watermark library's block
  // of the same name. Two of them (`enabled`, `autoApply`) the settings table
  // now writes; `displayOrder` is still reserved. Reserving one costs nothing
  // and is worth doing while the schema is being republished anyway, because
  // adding a field later is an ops step in every environment.
  //
  // NONE OF THESE THREE IS FILTERED ON, unlike `isDeleted` above, and that is
  // what lets them stay optional: an SDO filter matches the stored value, so a
  // record written without a FILTERED key matches nothing and vanishes. As long
  // as these are read client-side and never appear in a filter, a record that
  // omits one is fine. Putting any of them in a filter means making it required
  // and republishing — that is the whole cost, and it is not negotiable.

  /**
   * Position in the settings list.
   *
   * Reserved so drag-to-reorder can be added without another schema republish.
   * NOTHING SORTS BY IT YET — the table orders by name — exactly as
   * `displayOrder` sits unused on a watermark today.
   */
  readonly displayOrder?: number;
  /**
   * Whether the lexicon is offered in the upload selector and the editor's
   * apply panel.
   *
   * ABSENT MUST READ AS ENABLED — every record written before the column
   * existed omits it, and defaulting the other way would switch off an org's
   * whole library at once. `selectApplicableLexicons` is the single place that
   * decision is made, and it is the only place the flag is read.
   *
   * It hides a lexicon from the pickers; it does NOT retract it from documents
   * that already applied it. Their detections carry snapshots (U5-FR2), which
   * is the same property that makes a watermark's disable safe.
   */
  readonly enabled?: boolean;
  /**
   * Whether the lexicon should be applied without anyone choosing it.
   *
   * Honoured by `documents/sagas/lexiconAutoApply`, on the same terms as the
   * watermark equivalent: FIRST OPEN ONLY — a document saved even once is never
   * auto-applied to again, whatever the library says. That is what makes
   * removing an auto-apply lexicon stick; without it the next open would put it
   * back and nothing the reviewer did could stop it.
   *
   * Applying a lexicon is a budgeted worker run over every page (ADR-001), not
   * a stamp, and it writes a record per match into the document's asset — so
   * this flag is a decision about WORK, not only about defaults. That is why it
   * is per-lexicon and set by an admin, rather than everything applying by
   * default.
   *
   * Absent reads as OFF, which is the opposite of `enabled` beside it and is
   * right for the same reason: the safe default is the one that does nothing.
   *
   * CURRENTLY HIDDEN AND INERT — `LEXICON_AUTO_APPLY_ENABLED` is false, the
   * settings column is not rendered and the saga returns immediately. The
   * field is kept rather than removed so a value set beforehand survives. See
   * `autoApplyConfig` for why, and what has to be decided before it returns.
   */
  readonly autoApply?: boolean;
  /**
   * RESERVED. Whether a lexicon's matches are promoted straight to redactions
   * when it is applied, rather than arriving as detections to review.
   *
   * NOTHING WRITES OR READS IT YET. It is here because the schema republish is
   * pending and adding a property afterwards is an ops step in every
   * environment — the same reasoning that reserved `displayOrder`.
   *
   * AUTO-APPLY AND AUTO-REDACT ARE DIFFERENT AXES, and keeping them apart is
   * the point of reserving a second flag rather than overloading the first.
   * `autoApply` decides whether a lexicon RUNS; this decides whether what it
   * finds is trusted enough to skip review. Merging them is how the cheap
   * decision quietly acquires the expensive one's consequences: a lexicon
   * someone enabled to see what it would catch would instead start burning
   * text out of documents.
   *
   * Which is also why it will need more than a checkbox when it is built. A
   * redaction that nobody reviewed is the one outcome this product exists to
   * make deliberate, so at minimum: absent reads as OFF, it must be per
   * lexicon rather than global, and the applied record should say the
   * detections were never reviewed — `source: 'auto'` already carries half of
   * that for `autoApply`.
   */
  readonly autoRedact?: boolean;
}

/** The detail half: the entries, and nothing else worth reading. */
export interface LexiconEntriesRecord {
  /** SDO id of the detail record — a summary's `entriesId`. */
  readonly id: string;
  /** Back-reference, so an orphaned detail can be recognised as one. */
  readonly lexiconId: string;
  /** Guards every entry write (BR-6). */
  readonly revision: number;
  readonly entries: readonly LexiconEntry[];
}

/**
 * The case-folded name a uniqueness query can match EXACTLY.
 *
 * BR-2 is case-insensitive — "Case Terms" and "case terms" are the same name,
 * because the two are indistinguishable in the Categories dropdown, the picker
 * and the audit trail, where `lexiconName` is the only lexicon identifier
 * NFR-S7 permits. The platform's `filter` is exact-match, so it cannot answer a
 * case-insensitive question; this field moves the folding to WRITE time, where
 * it happens once, and lets the check be one query instead of a scan of every
 * lexicon in the org.
 *
 * Derived, never authored. `toSummaryData` computes it from `name` on every
 * write, which is what makes it impossible to save one without the other.
 */
export const lexiconNameKey = (name: string): string =>
  name.trim().toLowerCase();

/**
 * A lexicon's summary `data`.
 *
 * `recordPart` says which half of a lexicon this record is, and it is the key
 * both list queries filter on. It was `recordKind`, valued `lexicon` and
 * `entries`, which read as though one of the two records WAS the lexicon and
 * the other were an attachment to it. Both are the lexicon; each is a part of
 * it. `Part` also stays clear of the two words already spoken for on an
 * ENTRY — `type` (`term` / `regex`) and the author-facing kind (Keyword /
 * Pattern) — which is how one of them became confusing in the first place.
 *
 * WRITTEN AND FILTERED, NEVER READ BACK. `lexiconSummaryFromSdo` and
 * `lexiconEntriesFromSdo` tell the two apart by SHAPE (a detail has no `name`),
 * so this field exists for the server's benefit alone — which is why renaming
 * it could not break a read path.
 *
 * `nameLower` exists only on the wire too: nothing in the app reads it back,
 * and keeping it off `LexiconSummary` is what stops it being set by hand and
 * drifting from the name it is supposed to fold.
 */
export type LexiconSummaryData = Omit<LexiconSummary, 'id'> & {
  readonly recordPart: 'summary';
  readonly nameLower: string;
};

/** A lexicon's entries `data`. */
export type LexiconEntriesData = Omit<LexiconEntriesRecord, 'id'> & {
  readonly recordPart: 'entries';
  /**
   * Always `false`, and it means NOTHING here.
   *
   * A lexicon's deleted state lives on its summary, which is the only record
   * either list query reads. This is on the detail because `isDeleted` is
   * REQUIRED on every record in the published schema, and the invariant worth
   * having is "every record carries both filtered keys" rather than "every
   * record of one kind does" — the second is the version a future writer gets
   * wrong. See `docs/schemas/LexiconSchema.md`, "Required keys".
   */
  readonly isDeleted: false;
};

/**
 * A lexicon with its entries — the shape the drawer and the apply path use.
 *
 * Assembled from both records rather than stored as one, and still the unit
 * every mutation is expressed in: a caller says what the whole lexicon should
 * become and `updateLexiconGuarded` works out which records that touches. That
 * is deliberate. The audit trail, the revision guard and the fail-closed
 * ordering above it were built around one object and took three review rounds
 * to get right; splitting the STORAGE should not mean splitting them too.
 */
export interface Lexicon extends Omit<LexiconSummary, 'entryCount'> {
  readonly entries: readonly LexiconEntry[];
}

/**
 * A whole lexicon minus its id — what a mutation returns.
 *
 * Not what is stored. `updateLexiconGuarded` splits it across the summary and
 * detail records; see `LexiconSummaryData` and `LexiconEntriesData` for the
 * shapes that actually go on the wire.
 */
export type LexiconData = Omit<Lexicon, 'id'>;

/**
 * Immutable copy of the entry state that produced a detection, embedded in the
 * detection at application time.
 *
 * This is the mechanism behind U5-FR2 ("editing or deleting a lexicon does not
 * affect documents already redacted using it"). Because no consumer of document
 * state ever dereferences a live `Lexicon`, editing or hard-deleting one cannot
 * reach back into a document — the guarantee is structural rather than a policy
 * somebody has to remember. It is also what keeps the U5-FR15 source indicator
 * meaningful after its lexicon is deleted, and what satisfies risk R11 (a
 * lexicon edited after a bulk apply must not retroactively change assets that
 * were already redacted).
 *
 * Consequence, deliberately accepted: after a rename, an already-applied
 * detection shows the OLD name. That is correct — it records what was applied.
 */
export interface LexiconEntrySnapshot {
  readonly lexiconId: string;
  /** Name AS AT application time. Deliberately not re-resolved on read. */
  readonly lexiconName: string;
  readonly entryId: string;
  readonly term: string;
  readonly type: LexiconEntryType;
}

/**
 * Prefix of an entry id that exists only in the editor DRAFT (VE-24948 / U5).
 *
 * The dialog composes entries locally and commits them in one Save (matching
 * the design prototype); until then they carry a `draft:` id so the save can
 * tell "added" from "already persisted" without a second list. A real id (BR-8)
 * is assigned by the service on write and never starts with this prefix.
 */
export const DRAFT_ENTRY_ID_PREFIX = 'draft:';

export function isDraftEntryId(entryId: string): boolean {
  return entryId.startsWith(DRAFT_ENTRY_ID_PREFIX);
}

/**
 * The user-facing kind of an entry. It is now a pure READING OF THE TYPE, and
 * the point of the change is that there is nothing else to read.
 *
 * KEYWORD AND PHRASE ARE MERGED INTO `term`. They never differed in storage
 * (`LexiconEntryType` has two values, not three) and never differed in
 * matching — the match worker was never told which one an entry was. The only
 * behaviour that ever turned on the distinction was whether a comma in the
 * input split it into several entries while ADDING, which is a property of one
 * keystroke and not a kind of thing. It is now a visible toggle in the
 * composer, which is what it always was underneath.
 *
 * Two things follow, and both are simplifications rather than trade-offs:
 *
 *   - The `kind` FIELD is no longer written. With one non-pattern kind it
 *     carries no information the type does not, so a term entry stores nothing
 *     extra and `pattern` stays what it always was — a reading of
 *     `type: 'regex'`. Entries written earlier may still carry `kind:
 *     'keyword'` or `'phrase'` in the registry; nothing reads it, and it needs
 *     no migration to be harmless (see `docs/schemas/LexiconSchema.md`).
 *   - The WHITESPACE HEURISTIC is gone. "A phrase is a keyword containing
 *     whitespace" was a reasonable description of an unlabelled term and a bad
 *     contradiction of an author who had been asked; with nothing to
 *     distinguish, it has nothing left to get wrong.
 *
 * KEYWORD is the surviving word, not Term or Text. It is the one the authors
 * of this feature already use, a multi-word keyword is still a keyword, and it
 * is already in the CSV's accepted vocabulary — so an export says `keyword` and
 * re-imports with nothing to translate. `term` stays the STORAGE type and the
 * name of the field holding the text, which a regex entry has too; keeping the
 * two words apart is what stops "term" meaning two things.
 */
export type LexiconEntryKind = 'keyword' | 'pattern';

export function lexiconEntryKind(entry: {
  readonly type: LexiconEntryType;
}): LexiconEntryKind {
  return entry.type === 'regex' ? 'pattern' : 'keyword';
}

/** A new entry before it has an id — the shape the CSV importer produces. */
export interface LexiconEntryInput {
  readonly term: string;
  readonly type: LexiconEntryType;
  readonly caseSensitive?: boolean;
  readonly wholeWord?: boolean;
  readonly redactionCodeId?: string;
}

// ---------------------------------------------------------------------------
// CSV import
// ---------------------------------------------------------------------------

/**
 * The accepted CSV columns (U5-FR10). Order IN THE FILE is irrelevant — headers
 * are matched by name — but this is the order the exporter writes and the
 * dialog documents, so it is the order an author sees.
 *
 * THE THREE CODE COLUMNS ALL NAME THE SAME CODE, and the bare `redaction_code`
 * is the default one — it holds the DISPLAY CODE (`B6`), the string on the
 * redaction box and the one the import dialog asks for. The qualified
 * `redaction_code_name` and `redaction_code_id` are alternatives, each
 * sufficient on its own; when more than one is filled in the most HUMAN wins
 * (`redaction_code`, then name, then id — see `validateRow` on why the id goes
 * last). `HEADER_ALIASES` in `csvValidator` lists the other spellings an import
 * accepts.
 *
 * The bare name belongs to the display code because that is what the rest of
 * the app means by "Redaction Code" — it is the redaction report's column of
 * that name, and the string `redactionCodeAssignmentFor` puts on a box. The
 * other two say which of a code's fields they hold precisely because they are
 * not the one people mean by default.
 *
 * All three sit LAST because they are the only columns that are not about what
 * the entry matches, and the only ones whose values come from somewhere else
 * entirely (the Redaction Codes page).
 */
export const LEXICON_CSV_COLUMNS = [
  'term',
  'type',
  'case_sensitive',
  'whole_word',
  'redaction_code',
  'redaction_code_name',
  'redaction_code_id',
] as const;

export type LexiconCsvColumn = (typeof LEXICON_CSV_COLUMNS)[number];

/**
 * The parts of a redaction code the CSV modules need, and no more.
 *
 * STRUCTURAL ON PURPOSE. This module does not import `redactionCodes` — the
 * lexicon transport is injected rather than wired to its sibling registries
 * (see `actions/index.ts`) — and a code carries colour, size, overflow and
 * placement that a CSV has no opinion about. Callers pass their own
 * `RedactionCode` records straight in; the structural type accepts them.
 *
 * LIVE CODES ONLY. Both callers source these from `selectRedactionCodes` /
 * `ensureRedactionCodesLoaded`, which drop soft-deleted rows — so a deleted
 * code is not resolvable by name on import and is not named on export. That is
 * the intent: a deleted code no longer reserves its name (see the redaction
 * code duplicate check), so matching one could resolve to a row the author
 * cannot see.
 */
export interface LexiconCsvRedactionCode {
  readonly id: string;
  /** The short code stamped on the box, e.g. `B6` — "Display Code" in the UI. */
  readonly code: string;
  /** The human name, e.g. `Personal Information` — "Code Name" in the UI. */
  readonly codeName: string;
}

export interface LexiconCsvCellError {
  /**
   * 1-based row number AS THE USER'S SPREADSHEET NUMBERS IT (header is row 1,
   * first data row is row 2). An off-by-one here makes the error unactionable,
   * which defeats the whole point of cell-level reporting (U5-FR11).
   */
  readonly row: number;
  readonly column: LexiconCsvColumn;
  readonly message: string;
  /** Blocking errors prevent the upload completing (U5-FR12). */
  readonly blocking: boolean;
}

/** A file-level rejection: the CSV never got as far as per-cell validation. */
export interface LexiconCsvFileError {
  readonly message: string;
}

export type LexiconCsvValidationResult =
  | {
      readonly ok: true;
      readonly entries: readonly LexiconEntryInput[];
      /**
       * Terms already present in the target lexicon, skipped rather than
       * blocked (BR-23). Reported as a count so an additive re-upload of a
       * corrected superset does not become an error wall — which is what
       * blocking them would do to U5-FR13.
       */
      readonly skippedExistingCount: number;
    }
  | {
      readonly ok: false;
      readonly fileError?: LexiconCsvFileError;
      readonly cellErrors: readonly LexiconCsvCellError[];
      readonly skippedExistingCount: number;
    };

// ---------------------------------------------------------------------------
// Redux state
// ---------------------------------------------------------------------------

/**
 * Unsaved edits to a lexicon, held while the editor dialog is open.
 *
 * `entries` is the FULL desired list: persisted entries (real ids) plus ones
 * added in this session (`draft:` ids — see `DRAFT_ENTRY_ID_PREFIX`). Save
 * diffs it against the persisted lexicon: draft-id entries are appended,
 * persisted ids missing from it are removed.
 */
export interface LexiconDraft {
  readonly name: string;
  readonly entries: readonly LexiconEntry[];
}

export interface LexiconEditState {
  /** Absent when creating. */
  readonly lexiconId?: string;
  readonly draft: LexiconDraft;
  /** The `revision` read when the drawer opened — what BR-6 compares against. */
  readonly baseRevision?: number;
  /**
   * The lexicon's name and persisted entry ids AS READ WHEN THE DRAWER OPENED
   * — the baseline `selectDraftDiff` diffs the draft against. Deliberately a
   * snapshot rather than the live `lexicons[id]`: the list refreshes while
   * the drawer is open (every mutation dispatches FETCH_LEXICONS), and
   * diffing against a moved baseline turned another admin's concurrent
   * additions into this save's `removedEntryIds` (review-gate finding 3).
   * Advanced only by the BR-6 conflict Reload flow, together with
   * `baseRevision`.
   */
  readonly baseline?: {
    readonly name: string;
    /**
     * The entries as they stood when the drawer opened, in full.
     *
     * Ids alone were enough while a draft could only ADD or REMOVE. Editing an
     * entry in place changes its content without changing its id, so an id list
     * cannot see the change at all — which is precisely how an edit used to be
     * discarded in silence (it appeared in neither `added` nor
     * `removedEntryIds`). The diff needs the old CONTENT to compare against.
     */
    readonly entries: readonly LexiconEntry[];
  };
  readonly nameError?: string;
  /** Keyed by `entryId`, or by a draft-local key for unsaved entries. */
  readonly entryErrors: Readonly<Record<string, string>>;
  readonly conflict?: {
    readonly modifiedBy: string;
    readonly modifiedDateTime: string;
  };
}

export interface LexiconCsvImportState {
  readonly fileName: string;
  readonly rowCount: number;
  readonly result?: LexiconCsvValidationResult;
}

/**
 * A mutation outcome the page must show the user (NFR-S4's UX half).
 *
 * Three kinds, because they answer "did my change happen?" differently:
 *   - `blocked`          — NOT applied: the audit intent could not be recorded,
 *                          so the write never ran (fail-closed, ADR-003)
 *   - `audit-incomplete` — APPLIED, but the trail shows an intent without an
 *                          outcome; a reviewer may need to resolve it
 *   - `failed`           — NOT applied: an ordinary error
 * Collapsing these into one generic message would leave the user guessing
 * whether their change partly happened — the exact ambiguity fail-closed
 * exists to remove.
 */
export interface LexiconMutationNotice {
  readonly kind: 'blocked' | 'audit-incomplete' | 'failed';
  readonly message: string;
}

export interface LexiconsState {
  readonly isFetching: boolean;
  /**
   * True while the DELETED list is in flight, tracked apart from `isFetching`.
   *
   * The settings page asks for both lists at once, so one flag could not say
   * which had landed — and the table leaves the deleted view when it empties.
   * Against a shared flag that check fires on the gap between the two
   * responses and bounces the user straight back out of the trash.
   */
  readonly isFetchingDeleted: boolean;
  /** True while a create/rename/add/remove/delete is in flight. */
  readonly isMutating: boolean;
  readonly mutationNotice?: LexiconMutationNotice;
  /**
   * Every LIVE lexicon in the org — summaries, without entries.
   *
   * Entries reach the store only through `editing.draft`, which is hydrated
   * when the drawer opens. Holding them here would put every entry of every
   * lexicon in redux to render a table of names, which is the cost the storage
   * split exists to remove.
   *
   * THE WHOLE LIST, NOT A PAGE, and that is what makes this slot mean one
   * thing. It used to hold a page of whichever view the settings page was on,
   * so what "lexicons" meant depended on where the user had last been — the
   * editor inherited the trash after a visit to Deleted Lexicons and hid its
   * Lexicon button (VE-30783). Paging and sorting are the table's own business
   * now, over rows it already has.
   */
  readonly lexicons: Readonly<Record<string, LexiconSummary>>;
  /**
   * Every SOFT-DELETED lexicon in the org, for the deleted view and the
   * "View deleted lexicons (N)" link.
   *
   * A slot of its own rather than rows mixed into `lexicons` and sieved apart:
   * the two are two filtered queries (`isDeleted: 'false'` / `'true'`), so the
   * editor's fetch never carries the trash over the wire, and the count on the
   * link is `Object.keys(...).length` rather than a query of its own.
   */
  readonly deletedLexicons: Readonly<Record<string, LexiconSummary>>;
  readonly isDrawerOpen: boolean;
  readonly editing?: LexiconEditState;
  readonly csvImport?: LexiconCsvImportState;
  /**
   * True when `lexiconRegistryId` is absent from runtime config or its schema
   * cannot be resolved. NOT an error state (NFR-U5-12): it is the real state in
   * an environment where the registry has not been provisioned yet — GOV
   * before its bootstrap runs — so the feature is hidden, not broken.
   */
  readonly isNotConfigured: boolean;
}
